import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Typography,
  Grid,
  Divider,
  Box,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from "@material-ui/core";
import {
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
  Code as CodeIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import defaultLogoFavicon from "../../assets/favicon.ico";
import useSettings from "../../hooks/useSettings";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  logo: {
    width: 64,
    height: 64,
    marginRight: theme.spacing(2),
    borderRadius: 12,
    objectFit: "contain",
  },
  title: {
    fontWeight: 600,
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  card: {
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  cardIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  cardTitle: {
    fontWeight: 500,
  },
  versionChip: {
    marginLeft: theme.spacing(1),
  },
  statusOnline: {
    color: theme.palette.success.main,
  },
  statusOffline: {
    color: theme.palette.error.main,
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  infoLabel: {
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
  },
  infoValue: {
    fontWeight: 500,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  refreshButton: {
    marginLeft: "auto",
  },
  footer: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
  },
  footerText: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
  },
  featureIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.success.main,
    fontSize: "1rem",
  },
}));

const About = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const colorMode = useContext(ColorModeContext);
  const { getPublicSetting } = useSettings();
  const [loading, setLoading] = useState(false);
  const [systemInfo, setSystemInfo] = useState(null);
  const [checkingVersion, setCheckingVersion] = useState(false);
  const [faviconUrl, setFaviconUrl] = useState("");

  // Versão do frontend (pode ser definida via variável de ambiente ou package.json)
  const frontendVersion = process.env.REACT_APP_VERSION || "5.0.4";
  
  const fetchSystemInfo = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/system/info");
      if (data.success) {
        setSystemInfo(data.data);
      }
    } catch (error) {
      console.error("Erro ao buscar informações do sistema:", error);
      // Usar valores padrão se a API falhar
      setSystemInfo({
        nodeVersion: "N/A",
        platform: "N/A",
        uptime: 0,
        memoryUsage: { heapUsed: 0, heapTotal: 0 },
        whatsappVersion: { currentVersion: "N/A", isInitialized: false },
        timestamp: new Date().toISOString()
      });
    }
    setLoading(false);
  };

  const checkForUpdates = async () => {
    setCheckingVersion(true);
    try {
      const { data } = await api.post("/system/whatsapp-version/check");
      if (data.success) {
        if (data.data.updated || data.data.hasNewVersion) {
          toast.success(i18n.t("settings.components.about.toasts.whatsappUpdated", { version: data.data.newVersion }));
        } else {
          toast.info(i18n.t("settings.components.about.toasts.whatsappAlreadyUpdated"));
        }
        // Recarregar informações do sistema
        await fetchSystemInfo();
      }
    } catch (error) {
      // Se não for super admin, apenas mostrar info
      if (error.response?.status === 403) {
        toast.info(i18n.t("settings.components.about.toasts.adminOnly"));
      } else {
        toast.error(i18n.t("settings.components.about.toasts.checkError"));
      }
    }
    setCheckingVersion(false);
  };

  useEffect(() => {
    fetchSystemInfo();
  }, []);

  useEffect(() => {
    let mounted = true;

    getPublicSetting("appLogoFavicon", user?.companyId)
      .then((file) => {
        if (!mounted) return;
        setFaviconUrl(file ? `${getBackendUrl()}/public/${file}` : "");
      })
      .catch(() => {
        if (!mounted) return;
        setFaviconUrl("");
      });

    return () => {
      mounted = false;
    };
  }, [getPublicSetting, user?.companyId]);

  const formatUptime = (seconds) => {
    if (!seconds) return "N/A";
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    
    return parts.join(" ") || "< 1m";
  };

  const formatBytes = (bytes) => {
    if (!bytes) return "N/A";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  const features = [
    i18n.t("settings.components.about.features.multichannel"),
    i18n.t("settings.components.about.features.tickets"),
    i18n.t("settings.components.about.features.chatbot"),
    i18n.t("settings.components.about.features.campaigns"),
    i18n.t("settings.components.about.features.reports"),
    i18n.t("settings.components.about.features.apis"),
    i18n.t("settings.components.about.features.agents"),
    i18n.t("settings.components.about.features.schedules"),
  ];

  const appLogo = faviconUrl || colorMode.appLogoFavicon || defaultLogoFavicon;

  const appName = colorMode.appName || "TicketWhats";

  return (
    <div className={classes.root}>
      {/* Header com logo e nome */}
      <div className={classes.header}>
        <img
          src={appLogo}
          alt="Logo"
          className={classes.logo}
          onError={(e) => {
            e.target.src = defaultLogoFavicon;
          }}
        />
        <div>
          <Typography variant="h4" className={classes.title}>
            {appName}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {i18n.t("settings.components.about.subtitle")}
          </Typography>
        </div>
        <Chip
          label={`v${frontendVersion}`}
          color="primary"
          size="small"
          className={classes.versionChip}
        />
      </div>

      <Divider className={classes.divider} />

      <Grid container spacing={3}>
        {/* Card de Versões */}
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <div className={classes.cardHeader}>
                <CodeIcon className={classes.cardIcon} />
                <Typography variant="h6" className={classes.cardTitle}>
                  {i18n.t("settings.components.about.systemVersions")}
                </Typography>
                <Tooltip title={i18n.t("settings.components.about.checkUpdates")}>
                  <IconButton
                    size="small"
                    className={classes.refreshButton}
                    onClick={checkForUpdates}
                    disabled={checkingVersion}
                  >
                    {checkingVersion ? (
                      <CircularProgress size={20} />
                    ) : (
                      <RefreshIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </div>
              
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>
                  <BusinessIcon fontSize="small" style={{ marginRight: 8 }} />
                  Frontend
                </span>
                <span className={classes.infoValue}>v{frontendVersion}</span>
              </div>
              
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>
                  <StorageIcon fontSize="small" style={{ marginRight: 8 }} />
                  Backend
                </span>
                <span className={classes.infoValue}>v5.0.4</span>
              </div>
              
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>
                  <WhatsAppIcon fontSize="small" style={{ marginRight: 8 }} />
                  WhatsApp Web
                </span>
                <span className={classes.infoValue}>
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    systemInfo?.whatsappVersion?.currentVersion || "N/A"
                  )}
                </span>
              </div>
              
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>
                  <MemoryIcon fontSize="small" style={{ marginRight: 8 }} />
                  Node.js
                </span>
                <span className={classes.infoValue}>
                  {loading ? <CircularProgress size={16} /> : systemInfo?.nodeVersion || "N/A"}
                </span>
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Card de Status do Sistema */}
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <div className={classes.cardHeader}>
                <SpeedIcon className={classes.cardIcon} />
                <Typography variant="h6" className={classes.cardTitle}>
                  {i18n.t("settings.components.about.systemStatus")}
                </Typography>
                <Tooltip title={i18n.t("settings.components.about.refreshInfo")}>
                  <IconButton
                    size="small"
                    className={classes.refreshButton}
                    onClick={fetchSystemInfo}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Tooltip>
              </div>
              
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>
                  <CheckCircleIcon fontSize="small" className={classes.statusOnline} style={{ marginRight: 8 }} />
                  {i18n.t("common.statusLabel")}
                </span>
                <Chip
                  label={i18n.t("settings.components.about.online")}
                  size="small"
                  style={{ backgroundColor: "#4caf50", color: "white" }}
                />
              </div>
              
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>
                  <ScheduleIcon fontSize="small" style={{ marginRight: 8 }} />
                  Uptime
                </span>
                <span className={classes.infoValue}>
                  {loading ? <CircularProgress size={16} /> : formatUptime(systemInfo?.uptime)}
                </span>
              </div>
              
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>
                  <MemoryIcon fontSize="small" style={{ marginRight: 8 }} />
                  {i18n.t("settings.components.about.memoryUsage")}
                </span>
                <span className={classes.infoValue}>
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    formatBytes(systemInfo?.memoryUsage?.heapUsed)
                  )}
                </span>
              </div>
              
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>
                  <StorageIcon fontSize="small" style={{ marginRight: 8 }} />
                  {i18n.t("settings.components.about.platform")}
                </span>
                <span className={classes.infoValue}>
                  {loading ? <CircularProgress size={16} /> : systemInfo?.platform || "N/A"}
                </span>
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Card de Recursos */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardContent>
              <div className={classes.cardHeader}>
                <SecurityIcon className={classes.cardIcon} />
                <Typography variant="h6" className={classes.cardTitle}>
                  {i18n.t("settings.components.about.availableFeatures")}
                </Typography>
              </div>
              
              <Grid container spacing={2}>
                {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <div className={classes.featureItem}>
                      <CheckCircleIcon className={classes.featureIcon} />
                      <Typography variant="body2">{feature}</Typography>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box className={classes.footer}>
        <Typography className={classes.footerText}>
          TicketWhats © {new Date().getFullYear()} - {i18n.t("settings.components.about.subtitle")}
        </Typography>
        <Typography className={classes.footerText} style={{ marginTop: 8 }}>
          {i18n.t("settings.components.about.footer")}
        </Typography>
        {user?.super && (
          <Typography className={classes.footerText} style={{ marginTop: 8, fontSize: "0.75rem" }}>
            {i18n.t("settings.components.about.companyId")}: {user.companyId} | {i18n.t("settings.components.about.user")}: {user.name} ({user.email})
          </Typography>
        )}
      </Box>
    </div>
  );
};

export default About;
